/** Browser-only PNG cards for the Flow 8 photo-help simulation. */

function canvasUrl(width, height, draw) {
  if (typeof document === "undefined") return "";
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";
  draw(ctx, width, height);
  return canvas.toDataURL("image/png");
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

let sampleUrl = "";
let summaryUrl = "";
let stepsUrl = "";

const INK = "#122118";
const GREEN = "#0B8F6A";

function setFont(ctx, size, weight) {
  ctx.font = `${weight} ${size}px "Segoe UI", "Cambria Math", sans-serif`;
}

function measure(ctx, text, size, weight) {
  setFont(ctx, size, weight);
  return ctx.measureText(text).width;
}

function paint(ctx, text, x, y, size, color, weight) {
  setFont(ctx, size, weight);
  ctx.fillStyle = color;
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.fillText(text, x, y);
  return ctx.measureText(text).width;
}

/** Stacked fraction. The bar sits on barY. */
function drawFraction(ctx, x, barY, numerator, denominator, size, color) {
  const numW = measure(ctx, numerator, size, "600");
  const denW = measure(ctx, denominator, size, "600");
  const w = Math.max(numW, denW) + size * 0.7;
  ctx.fillStyle = color;
  ctx.fillRect(x, barY, w, Math.max(3, size * 0.07));
  setFont(ctx, size, "600");
  ctx.fillStyle = color;
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.fillText(numerator, x + w / 2, barY - size * 0.22);
  ctx.fillText(denominator, x + w / 2, barY + size * 0.98);
  ctx.textAlign = "left";
  return { w, top: barY - size * 1.2, bottom: barY + size * 1.15 };
}

/** Radical whose bar covers a stacked fraction. The vinculum stays above the numerator. */
function drawRadicalFraction(ctx, x, top, numerator, denominator, size, color) {
  const hook = size * 0.95;
  const barY = top + size * 1.35;
  const frac = drawFraction(ctx, x + hook + size * 0.12, barY, numerator, denominator, size, color);
  const right = x + hook + size * 0.12 + frac.w + size * 0.28;
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(3, size * 0.08);
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(x + size * 0.06, barY - size * 0.15);
  ctx.lineTo(x + size * 0.36, barY + size * 0.2);
  ctx.lineTo(x + hook, top);
  ctx.lineTo(right, top);
  ctx.stroke();
  return { w: right - x, h: frac.bottom - top, axis: barY };
}

function drawRadicalText(ctx, x, baseline, text, size, color) {
  const w = measure(ctx, text, size, "600");
  const top = baseline - size * 0.95;
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(3, size * 0.07);
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(x, baseline - size * 0.15);
  ctx.lineTo(x + size * 0.22, baseline + size * 0.12);
  ctx.lineTo(x + size * 0.48, top);
  ctx.lineTo(x + size * 0.55 + w + size * 0.2, top);
  ctx.stroke();
  paint(ctx, text, x + size * 0.55, baseline, size, color, "600");
  return size * 0.55 + w + size * 0.25;
}

/** Textbook crop used when the student picks the example photo. */
export function sampleExerciseUrl() {
  if (sampleUrl) return sampleUrl;
  sampleUrl = canvasUrl(720, 980, (ctx) => {
    ctx.fillStyle = "#F7F4EC";
    ctx.fillRect(0, 0, 720, 980);
    ctx.fillStyle = "#1F3A2E";
    ctx.fillRect(0, 0, 720, 64);
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "600 28px Segoe UI, sans-serif";
    ctx.fillText("Maths — Sondage", 28, 42);

    ctx.fillStyle = "#122118";
    ctx.font = "700 26px Segoe UI, sans-serif";
    ctx.fillText("Taille de l'échantillon et marge d'erreur", 28, 112);
    ctx.font = "22px Segoe UI, sans-serif";
    ctx.fillText("p̂ = 0,30     z = 1,645 (confiance 90 %)", 28, 154);
    ctx.fillText("E = 1,645 × √( p̂ (1 − p̂) / n )", 28, 196);

    ctx.font = "700 22px Segoe UI, sans-serif";
    ctx.fillText("Élèves interrogés", 28, 258);
    ctx.fillText("Marge d'erreur", 360, 258);
    ctx.font = "22px Segoe UI, sans-serif";
    const rows = [
      ["100", "± 7,54 %"],
      ["400", "± 3,77 %"],
      ["1 000", "± 2,38 %"],
      ["4 000", "± 1,19 %"],
    ];
    rows.forEach((row, index) => {
      const y = 304 + index * 40;
      ctx.fillStyle = index % 2 ? "#EFEAE2" : "#FFFFFF";
      ctx.fillRect(28, y - 26, 640, 40);
      ctx.fillStyle = "#122118";
      ctx.fillText(row[0], 40, y);
      ctx.fillText(row[1], 360, y);
    });

    ctx.strokeStyle = "#0B8F6A";
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(80, 520);
    ctx.bezierCurveTo(180, 700, 280, 820, 640, 860);
    ctx.stroke();
    ctx.fillStyle = "#4a5f53";
    ctx.font = "20px Segoe UI, sans-serif";
    ctx.fillText("La courbe descend vite, puis de plus en plus lentement.", 28, 930);
  });
  return sampleUrl;
}

let pythagoreUrl = "";
let phraseUrl = "";
let pythagoreSheetUrl = "";
let pythagoreFormulaCache = "";

function drawPower(ctx, x, baseline, base, power, size, color) {
  const w = paint(ctx, base, x, baseline, size, color, "600");
  paint(ctx, power, x + w + 1, baseline - size * 0.55, Math.round(size * 0.55), color, "700");
  return w + measure(ctx, power, Math.round(size * 0.55), "700") + 4;
}

/** Right triangle page: legs 3 cm and 4 cm. */
export function pythagoreExerciseUrl() {
  if (pythagoreUrl) return pythagoreUrl;
  pythagoreUrl = canvasUrl(720, 780, (ctx) => {
    ctx.fillStyle = "#F7F4EC";
    ctx.fillRect(0, 0, 720, 780);
    ctx.fillStyle = "#1F3A2E";
    ctx.fillRect(0, 0, 720, 64);
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "600 28px Segoe UI, sans-serif";
    ctx.fillText("Maths — Triangle rectangle", 28, 42);
    ctx.fillStyle = "#122118";
    ctx.font = "700 26px Segoe UI, sans-serif";
    ctx.fillText("Calcule l'hypoténuse.", 28, 120);
    ctx.strokeStyle = "#0B8F6A";
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.moveTo(160, 560);
    ctx.lineTo(520, 560);
    ctx.lineTo(160, 220);
    ctx.closePath();
    ctx.stroke();
    ctx.fillStyle = "#122118";
    ctx.font = "700 28px Segoe UI, sans-serif";
    ctx.fillText("3 cm", 300, 600);
    ctx.fillText("4 cm", 80, 400);
    ctx.fillText("?", 360, 360);
    ctx.font = "22px Segoe UI, sans-serif";
    ctx.fillText("Angle droit entre les côtés de 3 cm et de 4 cm.", 28, 700);
  });
  return pythagoreUrl;
}

/** Grammar page: one sentence to split into subject and verb. */
export function phraseExerciseUrl() {
  if (phraseUrl) return phraseUrl;
  phraseUrl = canvasUrl(720, 520, (ctx) => {
    ctx.fillStyle = "#F7F4EC";
    ctx.fillRect(0, 0, 720, 520);
    ctx.fillStyle = "#1F3A2E";
    ctx.fillRect(0, 0, 720, 64);
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "600 28px Segoe UI, sans-serif";
    ctx.fillText("Français — La phrase", 28, 42);
    ctx.fillStyle = "#122118";
    ctx.font = "700 26px Segoe UI, sans-serif";
    ctx.fillText("Trouve le sujet et le verbe.", 28, 140);
    roundRect(ctx, 28, 190, 664, 140, 16);
    ctx.fillStyle = "#FFFFFF";
    ctx.fill();
    ctx.fillStyle = "#122118";
    ctx.font = "28px Segoe UI, sans-serif";
    ctx.fillText("Le petit chat noir dort", 52, 250);
    ctx.fillText("sur le canapé.", 52, 296);
  });
  return phraseUrl;
}

export function matchHelpItem(src) {
  if (!src) return "unknown";
  if (src === sampleExerciseUrl()) return "marge";
  if (src === pythagoreExerciseUrl()) return "pythagore";
  if (src === phraseExerciseUrl()) return "phrase";
  return "unknown";
}

/** Formula and the checked 3-4-5 triangle, without the next example. */
export function pythagoreFormulaUrl() {
  if (pythagoreFormulaCache) return pythagoreFormulaCache;
  pythagoreFormulaCache = canvasUrl(760, 640, (ctx, width, height) => {
    ctx.fillStyle = "#F4FBF7";
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = GREEN;
    ctx.fillRect(0, 0, width, 90);
    paint(ctx, "a² + b² = c²", 36, 58, 36, "#FFFFFF", "700");
    let x = 48;
    const y = 220;
    x += drawPower(ctx, x, y, "a", "2", 56, INK);
    x += paint(ctx, "  +  ", x, y, 40, INK, "400");
    x += drawPower(ctx, x, y, "b", "2", 56, INK);
    x += paint(ctx, "  =  ", x, y, 40, INK, "400");
    drawPower(ctx, x, y, "c", "2", 56, INK);
    paint(ctx, "Pour 3 cm et 4 cm", 48, 340, 26, GREEN, "700");
    let pen = 48;
    pen += drawPower(ctx, pen, 430, "3", "2", 34, INK);
    pen += paint(ctx, " + ", pen, 430, 30, INK, "400");
    pen += drawPower(ctx, pen, 430, "4", "2", 34, INK);
    paint(ctx, "  =  9 + 16  =  25", pen, 430, 30, INK, "600");
    paint(ctx, "c  =  5 cm", 48, 530, 34, GREEN, "700");
  });
  return pythagoreFormulaCache;
}
export function pythagoreSummaryUrl() {
  if (pythagoreSheetUrl) return pythagoreSheetUrl;
  pythagoreSheetUrl = canvasUrl(760, 980, (ctx, width, height) => {
    ctx.fillStyle = "#F4FBF7";
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = GREEN;
    ctx.fillRect(0, 0, width, 110);
    paint(ctx, "Pythagore", 36, 54, 40, "#FFFFFF", "700");
    paint(ctx, "La formule, en notation réelle", 36, 92, 22, "#E9FBF0", "400");

    roundRect(ctx, 28, 150, width - 56, 200, 18);
    ctx.fillStyle = "#FFFFFF";
    ctx.fill();
    let x = 70;
    const y = 270;
    x += drawPower(ctx, x, y, "a", "2", 64, INK);
    x += paint(ctx, "  +  ", x, y, 48, INK, "400");
    x += drawPower(ctx, x, y, "b", "2", 64, INK);
    x += paint(ctx, "  =  ", x, y, 48, INK, "400");
    drawPower(ctx, x, y, "c", "2", 64, INK);

    paint(ctx, "Triangle 3-4-5", 36, 420, 28, GREEN, "700");
    let pen = 36;
    const row = 500;
    pen += drawPower(ctx, pen, row, "3", "2", 36, INK);
    pen += paint(ctx, " + ", pen, row, 32, INK, "400");
    pen += drawPower(ctx, pen, row, "4", "2", 36, INK);
    pen += paint(ctx, "  =  9 + 16  =  25", pen, row, 32, INK, "600");
    paint(ctx, "c  =  5 cm", 36, 580, 36, GREEN, "700");

    paint(ctx, "Exemple 6-8-10", 36, 680, 28, GREEN, "700");
    pen = 36;
    pen += drawPower(ctx, pen, 760, "6", "2", 36, INK);
    pen += paint(ctx, " + ", pen, 760, 32, INK, "400");
    pen += drawPower(ctx, pen, 760, "8", "2", 36, INK);
    paint(ctx, "  =  36 + 64  =  100", pen, 760, 32, INK, "600");
    paint(ctx, "c  =  10 cm", 36, 860, 36, GREEN, "700");
  });
  return pythagoreSheetUrl;
}

/** Worked n = 100 steps, each equation drawn on its own row. */
export function formulaStepsUrl() {
  if (stepsUrl) return stepsUrl;
  stepsUrl = canvasUrl(700, 780, (ctx, width, height) => {
    ctx.fillStyle = "#F4FBF7";
    ctx.fillRect(0, 0, width, height);
    paint(ctx, "n = 100", 36, 58, 28, GREEN, "700");
    drawWorkedHundred(ctx, 36, 100);
  });
  return stepsUrl;
}

function drawWorkedHundred(ctx, x, y) {
  const s = 32;
  let cursor = y;
  const row = (draw, advance) => {
    draw(cursor);
    cursor += advance;
  };
  row((baseline) => {
    let pen = x;
    pen += paint(ctx, "1 − 0,30  =  0,70", pen, baseline, s, INK, "600") ;
  }, 78);
  row((baseline) => {
    paint(ctx, "0,30 × 0,70  =  0,21", x, baseline, s, INK, "600");
  }, 110);
  row((baseline) => {
    const frac = drawFraction(ctx, x, baseline - 8, "0,21", "100", s, INK);
    paint(ctx, "=  0,0021", x + frac.w + 18, baseline + 8, s, INK, "600");
  }, 120);
  row((baseline) => {
    const w = drawRadicalText(ctx, x, baseline, "0,0021", s, INK);
    paint(ctx, "≈  0,04583", x + w + 16, baseline, s, INK, "600");
  }, 90);
  row((baseline) => {
    paint(ctx, "1,645 × 0,04583  ≈  0,0754", x, baseline, s, INK, "600");
  }, 70);
  return cursor;
}

/**
 * Summary image: the formula is built like a textbook,
 * with a radical over a real fraction. Rounded values stay
 * on their own equation, with ≈ .
 */
export function visualSummaryUrl() {
  if (summaryUrl) return summaryUrl;
  summaryUrl = canvasUrl(760, 1760, (ctx, width, height) => {
    ctx.fillStyle = "#F4FBF7";
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = GREEN;
    ctx.fillRect(0, 0, width, 118);
    paint(ctx, "Marge d'erreur", 36, 58, 40, "#FFFFFF", "700");
    paint(ctx, "La formule, en notation réelle", 36, 96, 22, "#E9FBF0", "400");

    const size = 42;
    const num = "p̂ (1 − p̂)";
    const den = "n";
    const lead = "E  =  1,645  ×";
    const leadW = measure(ctx, lead, 44, "600");
    const fracW = Math.max(measure(ctx, num, size, "600"), measure(ctx, den, size, "600")) + size * 0.7;
    const radW = size * 0.95 + size * 0.12 + fracW + size * 0.28;
    const origin = Math.max(36, (width - (leadW + 18 + radW)) / 2);
    const formulaTop = 248;

    roundRect(ctx, 28, 168, width - 56, 280, 18);
    ctx.fillStyle = "#FFFFFF";
    ctx.fill();
    paint(ctx, "Formule", 48, 206, 24, GREEN, "700");

    const rad = drawRadicalFraction(ctx, origin + leadW + 18, formulaTop, num, den, size, INK);
    paint(ctx, lead, origin, rad.axis + 12, 44, INK, "600");

    paint(ctx, "p̂ = 0,30      1,645 = confiance 90 %      n = élèves", 36, 490, 22, "#4a5f53", "400");

    paint(ctx, "Avec 100 élèves", 36, 560, 28, GREEN, "700");
    const afterSteps = drawWorkedHundred(ctx, 36, 610);

    roundRect(ctx, 28, afterSteps + 16, width - 56, 96, 16);
    ctx.fillStyle = "#E9FBF0";
    ctx.fill();
    paint(ctx, "E  =  0,0754  =  7,54 %", 52, afterSteps + 76, 36, GREEN, "700");

    const block = afterSteps + 170;
    paint(ctx, "Avec 400 élèves", 36, block, 28, GREEN, "700");
    const fSize = 36;
    const lead400 = "E  =  1,645  ×";
    const lead400W = measure(ctx, lead400, 36, "600");
    const rad400 = drawRadicalFraction(ctx, 36 + lead400W + 16, block + 24, "0,21", "400", fSize, INK);
    paint(ctx, lead400, 36, rad400.axis + 10, 36, INK, "600");

    const eqY = block + 24 + rad400.h + 56;
    const exact = drawFraction(ctx, 36, eqY, "0,21", "400", 34, INK);
    paint(ctx, "=  0,000525", 36 + exact.w + 18, eqY + 8, 34, INK, "600");
    paint(ctx, "E  ≈  3,77 %", 36, eqY + 130, 40, GREEN, "700");
  });
  return summaryUrl;
}

/** Typeset a formula structure returned by the vision model. Numbers stay in the layout, not in a generated picture. */
export function modelFormulaUrl(title, parts) {
  const rows = (Array.isArray(parts) ? parts : []).slice(0, 8);
  const height = 200 + rows.length * 150;
  return canvasUrl(760, height, (ctx, width) => {
    ctx.fillStyle = "#F4FBF7";
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = GREEN;
    ctx.fillRect(0, 0, width, 108);
    paint(ctx, String(title || "Résumé visuel").slice(0, 42), 36, 68, 34, "#FFFFFF", "700");
    let y = 168;
    rows.forEach((part) => {
      if ((part.kind === "radical" || part.kind === "fraction") && part.numerator && part.denominator) {
        const size = 36;
        const lead = part.left ? `${part.left}  ` : "";
        const leadW = lead ? measure(ctx, lead, 32, "600") : 0;
        if (part.kind === "radical") {
          const rad = drawRadicalFraction(ctx, 36 + leadW, y, part.numerator, part.denominator, size, INK);
          if (lead) paint(ctx, lead, 36, rad.axis + 10, 32, INK, "600");
          y += rad.h + 36;
        } else {
          const frac = drawFraction(ctx, 36 + leadW, y + size, part.numerator, part.denominator, size, INK);
          if (lead) paint(ctx, lead, 36, y + size + 8, 32, INK, "600");
          y += frac.bottom - y + 28;
        }
        return;
      }
      if (part.text) {
        paint(ctx, String(part.text).slice(0, 72), 36, y + 28, 26, INK, "600");
        y += 64;
      }
    });
  });
}
